import Sortable from 'sortablejs';
import ApiService from '@/services/apiService';
import iconsMixin from '@/components/core/dtwin/state/bridge/iconsMixin';
import dataSourcesListMixin from '@/components/core/dtwin/state/dataSourcesListMixin';
import { mapState } from 'vuex';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
import { userItemsPerListMixin } from '@/utils/vuex';
import CoreDtwinStateEdit from '@/components/core/dtwin/state/edit';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
const defaultExportButton = {
  icon: 'fal fa-file-csv',
  action: 'exportModal',
  tooltip: 'Core.ExportDialog.titleTextCsv'
};
export default {
  name: 'CoreDtwinStateTable',
  components: {
    codemirror,
    CoreDtwinStateEdit
  },
  mixins: [iconsMixin, dataSourcesListMixin, userItemsPerListMixin],
  props: {
    // on the twin-overview page display just the bare minimum, without search and action buttons
    // options: null, lite, readonly, editonly
    mode: {
      type: String,
      required: false,
      default: () => null
    },
    tenantId: {
      type: Number,
      required: true
    },
    // the states that are to be displayed / edited
    value: {
      type: Array,
      required: true
    },
    updatedStates: {
      type: Array,
      required: false,
      default: () => []
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinManagePermission: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinEditPermission: {
      type: Boolean,
      required: false,
      default: false
    },
    deviceTemplate: {
      type: Object,
      required: false,
      default: null
    },
    isVirtualDevice: {
      type: Boolean,
      required: true
    },
    // the original twin(/virtual-device)-model
    // to be used by some components for additional requesting data
    twinModel: {
      type: Object,
      required: false,
      default: () => {
        return {};
      }
    },
    editDialogComponent: {
      type: String,
      required: false,
      default: () => 'core-dtwin-state-edit'
    },
    exportButtonDetails: {
      type: Object,
      required: false,
      default: () => defaultExportButton
    },
    // optionally provide an own name-mapping for datasources
    sourcesNameMapping: {
      type: Object,
      required: false,
      default: () => {
        return {};
      }
    }
  },
  data: function () {
    return {
      showDeleteDialog: false,
      confirmDeleteId: null,
      linkedStateAggregations: null,
      blockStateOrdering: false,
      sortedStates: null,
      ruleType: null,
      bridgeConfigs: [],
      stateEditDialog: false,
      stateEditServerRequest: false,
      stateEditItem: null,
      stateNiotaImportDialog: false,
      // stateBulkCreateDialog: false,

      showHistoryDialog: false,
      historyState: null,
      search: '',
      stateDisplayDialog: false,
      cmOptions: {
        tabSize: 4,
        mode: 'application/ld+json',
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        readOnly: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false,
        viewportMargin: Infinity,
        exportDialog: false
      },
      tableOptions: {
        ...defaultTableOptions
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    themeMode() {
      return Boolean(this.$store.state.app.UiTheme) ? 'dark' : 'light';
    },
    getStateEditMode() {
      if (this.isCreatedByTemplate) {
        return 'readonly';
      }
      if (!this.stateEditServerRequest && this.hasTwinEditPermission && this.mode !== 'readonly') {
        return this.stateEditItem == null ? 'create' : 'edit';
      } else {
        return 'readonly';
      }
    },
    detailButtonIcon() {
      return this.hasTwinEditPermission && this.mode !== 'readonly' ? 'fa-edit' : 'fa-eye';
    },
    actionButtons() {
      if (this.mode === 'lite') {
        return [];
      }
      return [{
        ...defaultExportButton,
        icon: get(this.exportButtonDetails, 'icon', defaultExportButton.icon),
        tooltip: this.$i18n_t(get(this.exportButtonDetails, 'tooltip', defaultExportButton.tooltip)),
        valid: this.value.length > 0 && this.$vuetify.breakpoint.smAndUp,
        dataCy: 'csv_export_btn'
      }, {
        icon: 'fal fa-eye',
        action: 'stateView',
        valid: this.value.length > 0 && this.$vuetify.breakpoint.smAndUp,
        tooltip: this.$i18n_t('Common.viewDetails'),
        dataCy: 'view_details_btn'
      }, this.mode === 'editonly' || this.mode === 'readonly' ? {} : {
        icon: 'fal fa-plus',
        action: 'addItem',
        disabled: this.isCreatedByTemplate,
        valid: !this.disabled && this.hasTwinEditPermission,
        tooltip: this.$i18n_t('Common.addNew'),
        dataCy: 'add_new_btn'
      }].filter(button => button.action);
    },
    isCreatedByTemplate() {
      return !!this.deviceTemplate;
    },
    canDeleteState() {
      return this.linkedStateAggregations && this.linkedStateAggregations.length === 0;
    },
    tableLocalizations() {
      return {
        dialogHeaders: [{
          text: ''
        }, {
          sortable: true,
          text: this.$i18n_t('Common.id'),
          value: 'id'
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.title'),
          value: 'title'
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.key'),
          value: 'key'
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
          value: 'stateValue'
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.unit'),
          value: 'unit'
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.lastUpdate'),
          value: 'stateTime'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.source'),
          align: 'center'
        }, {
          text: ''
        }, {
          text: ''
        }],
        headers: [{
          sortable: false,
          text: '',
          align: 'right'
        }, {
          sortable: false,
          text: ''
        }, {
          sortable: false,
          text: this.$i18n_t('Common.id'),
          value: 'id'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.title'),
          value: 'title'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
          value: 'stateValue'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.unit'),
          value: 'unit'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.source'),
          align: 'center'
        }, {
          sortable: false,
          text: '',
          align: 'center'
        }, {
          sortable: false,
          text: '',
          align: 'center'
        }],
        dialogFooterProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [10, 20, 50]
        }
      };
    }
  },
  watch: {
    updatedStates() {
      if (this.historyState && this.updatedStates.findIndex(s => s.id === this.historyState.id) >= 0) {
        if (this.$refs['dtwin-state-history']) {
          this.$refs['dtwin-state-history'].reloadData();
        }
      }
    },
    value() {
      this.initSortableTable();
    }
  },
  mounted() {
    this.tableOptions.itemsPerPage = this.savedUserItemsPerList();
    this.tableOptions.page = 1;
    this.$store.state.app.loading = true;
    this.initSortableTable();
    if (this.hasTwinEditPermission) {
      this.loadBridgeConfigs().then(configData => {
        const types = configData[0];
        const configs = configData[1];
        this.bridgeConfigs = configs.filter(config => get(this.bridgeConfig(config, types), 'manifest.functionScope.invokeDirection.inbound')).map(config => {
          return {
            type: config.type,
            image: this.bridgeTypesIcons[config.type],
            title: config.name,
            configId: config.id,
            tenantId: config.tenantId
          };
        });
      }).catch(error => {
        console.error('Error', error);
      }).finally(() => {
        this.$store.state.app.loading = false;
        this.$emit('finishedLoading');
      });
    } else {
      this.$store.state.app.loading = false;
      this.$emit('finishedLoading');
    }
  },
  methods: {
    handlePagination(options) {
      this.tableOptions.itemsPerPage = options.itemsPerPage;
      this.tableOptions.page = options.page;
    },
    isStateDeletable(state) {
      return state.key !== '_raw' && state.key !== '_health' && state.key !== '_parsed' || !this.isVirtualDevice;
    },
    fetchLinkedStateAggregations(stateId) {
      this.linkedStateAggregations = null;
      return ApiService.digitalTwinGetStateLinkedAggregations(this.$route.params.id, stateId).then(linkedStateAggregations => {
        linkedStateAggregations.forEach(state => {
          state.children = [{
            ...state.dtwin,
            icon: state.dtwin.twinType === 'virtualDevice' ? 'sensor-on' : 'dice-d8',
            isTwin: true
          }];
          state.title = `${state.title} (${state.key})`;
          state.icon = state.icon || 'layer-group';
        });
        this.linkedStateAggregations = linkedStateAggregations;
      }).catch(error => {
        console.error(error);
      });
    },
    detailTableHeaderClasses(props) {
      const sortBy = props.options.sortBy && props.options.sortBy.length ? props.options.sortBy[0] : 'none';
      const sortDesc = props.options.sortDesc && props.options.sortDesc.length && props.options.sortDesc[0];
      return {
        text: {
          title: sortBy === 'title' ? 'header-active' : 'header-nonactive',
          key: sortBy === 'key' ? 'header-active' : 'header-nonactive'
        },
        icon: {
          title: sortBy === 'title' ? sortDesc ? 'icon-desc' : '' : 'icon-nonactive',
          key: sortBy === 'key' ? sortDesc ? 'icon-desc' : '' : 'icon-nonactive'
        }
      };
    },
    detailTableHeaderColClass(header, props) {
      let classes = [`text-${header.align || 'start'}`];
      if (header.sortable) {
        classes.push('sortable');
      }
      const values = [header.value];
      if (header.value === 'title') {
        values.push('key');
      }
      if (props.options.sortBy && props.options.sortBy.length && values.indexOf(props.options.sortBy[0]) >= 0) {
        classes.push('active');
        if (props.options.sortDesc && props.options.sortDesc.length && props.options.sortDesc[0]) {
          classes.push('desc');
        } else {
          classes.push('asc');
        }
      }
      return classes.join(' ');
    },
    updateStatesOrder() {
      const orderArray = this.sortedStates.map(item => item.id);
      const twinId = this.$route.params.id;
      ApiService.digitalTwinUpdateStatesOrder(this, twinId, orderArray).then(() => {
        this.$emit('statesUpdated');
        this.blockStateOrdering = false;
      }).catch(error => {
        console.error(error);
      });
    },
    initSortableTable() {
      const table = document.querySelector('.states-table tbody');
      this.sortedStates = cloneDeep(this.value);
      Sortable.create(table, {
        handle: '.handle',
        onEnd: ({
          newIndex,
          oldIndex
        }) => {
          const pageOffset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
          this.blockStateOrdering = true;
          this.sortedStates.splice(newIndex + pageOffset, 0, ...this.sortedStates.splice(oldIndex + pageOffset, 1));
          this.updateStatesOrder();
        }
      });
    },
    bridgeConfig(bridge, types) {
      const foundMatch = types.find(config => {
        return config.type === bridge.type;
      });
      return foundMatch;
    },
    loadBridgeConfigs() {
      return Promise.all([ApiService.getBridgeTypes(), ApiService.getBridgesForTenant(this.tenantId)]);
    },
    editState(item) {
      if (this.mode === 'lite' && this.mode === 'readonly' || this.disabled) {
        // no editing in lite-view
        return;
      }
      if (!item.properties.binding.options) {
        item.properties.binding = Object.assign({}, item.properties.binding, {
          options: {}
        });
      }
      if ((item.properties.binding.options.aggregateStateBindingIds || []).length === 0) {
        item.properties.binding.options = Object.assign({}, item.properties.binding.options, {
          aggregateStateBindingIds: []
        });
      }
      this.stateEditItem = item;
      this.stateEditDialog = true;
    },
    localizeDate(item) {
      if (item.value && item.value.time) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(item.value.time).format('L LTS');
      }
      return '-';
    },
    // addStack () {
    //   this.stateBulkCreateDialog = true
    // },
    addItem() {
      this.openStateEditForm();
    },
    stateView() {
      this.stateDisplayDialog = true;
    },
    exportModal() {
      this.$emit('showExportModal');
    },
    outputValue(item) {
      if (item.value === null || item.value.value === null) {
        return 'null';
      }
      try {
        const jsonObject = typeof item.value.value === 'object' ? item.value.value : JSON.parse(item.value.value);
        // parse and then stringify again to get a nice format
        return JSON.stringify(jsonObject, null, 1);
      } catch (error) {
        // on error we return outside of try/catch
      }
      return item.value.value || 'null';
    },
    displayType(item) {
      if (typeof item.value.value === 'string') {
        if (item.value.value.length > 50) {
          return 'code';
        }
      }
      if (item.value === null || item.value.value === null) {
        return 'plain';
      }
      if (typeof item.value.value === 'object' || item.properties.valueType === 'json') {
        return 'code';
      }
      return 'plain';
    },
    openStateEditForm() {
      this.stateEditItem = null;
      this.stateEditDialog = true;
    },
    openNiotaImportForm() {
      this.stateNiotaImportDialog = true;
    },
    closeEditWithError(errorMessage) {
      if (errorMessage) {
        this.$snack(errorMessage, 'error');
      }
      this.stateEditDialog = false;
      this.stateEditItem = null;
    },
    saveState(stateDetails) {
      stateDetails.dtwinId = this.$route.params.id;
      let serverProcessingPromise = null;
      if (stateDetails.value.value === undefined) {
        stateDetails.value.value = null;
      }
      this.stateEditServerRequest = true;
      if (!stateDetails.id) {
        serverProcessingPromise = ApiService.digitalTwinCreateState(this, stateDetails).then(() => {
          this.$emit('statesUpdated');
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.states.created') + stateDetails.properties.title, 'success');
          this.stateEditDialog = false;
        });
      } else {
        serverProcessingPromise = ApiService.digitalTwinUpdateState(this, stateDetails).then(() => {
          this.$emit('statesUpdated');
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.states.updated') + stateDetails.properties.title, 'success');
          this.stateEditDialog = false;
        });
      }
      serverProcessingPromise.catch(error => {
        let responseMsg = get(error, 'response.data.data');
        if (this.$i18n_te(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          let outputErrorMsg = `${this.$i18n_t(`DigitalTwin.DigitalTwin.states.${stateDetails.id ? 'update' : 'create'}Error`)}`;
          if (responseMsg) {
            outputErrorMsg = `${outputErrorMsg}: ${responseMsg}`;
          }
          this.$snack(outputErrorMsg, 'error');
        }
      }).finally(() => {
        this.stateEditServerRequest = false;
      });
    },
    deleteState(id) {
      this.fetchLinkedStateAggregations(id);
      this.confirmDeleteId = id;
      this.showDeleteDialog = true;
    },
    handleConfirmDelete(id) {
      this.confirmDeleteId = null;
      const state = this.value.find(state => state.id === id);
      if (state) {
        ApiService.digitalTwinDeleteState(this, state.dtwinId, id).then(() => {
          this.$emit('statesUpdated');
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.states.deleted') + state.properties.title, 'success');
        }).catch(error => {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack('Could not delete state', 'error');
          }
        });
      }
    },
    async importNiota(deviceVariableImport) {
      this.stateNiotaImportDialog = false;
      const states = deviceVariableImport.variables.map(variable => {
        return {
          key: variable.key,
          dtwinId: this.$route.params.id,
          value: {
            value: variable.value,
            time: variable.time
          },
          properties: {
            title: variable.key,
            unit: variable.unit,
            binding: {
              type: 'bridge',
              options: {
                configId: deviceVariableImport.configId,
                identifier: deviceVariableImport.identifier,
                variable: variable.key
              }
            }
          }
        };
      });
      await this.createStates(states);
    },
    async createStates(states) {
      for (let i = 0; i < states.length; i++) {
        // TODO: show activity indicator
        await ApiService.digitalTwinCreateState(this, states[i]).then(() => {
          this.$emit('statesUpdated');
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.states.created') + states[i].properties.title, 'success');
        }).catch(error => {
          let responseMsg = get(error, 'response.data.message');
          if (this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            let createErrorMsg = `${this.$i18n_t('DigitalTwin.DigitalTwin.states.createError')}: ${states[i].properties.title}`;
            if (responseMsg) {
              createErrorMsg = `${createErrorMsg}: ${responseMsg}`;
            }
            this.$snack(createErrorMsg, 'error');
          }
        });
      }
      this.$emit('statesUpdated');
    },
    showHistory(a) {
      this.historyState = a;
      this.showHistoryDialog = true;
    }
  }
};